import { useMutation } from '@tanstack/react-query';

import { REPORTING_LE_LENS_DESCRIBE_PATH } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type { LensCardType } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

/** The one card shape the describe endpoint needs: enough to write a
 *  sentence about what it shows, nothing about how it was built. */
export type DescribeReportingLeLensCard = {
  id: string;
  title: string;
  type: LensCardType;
  columns: Array<string>;
  /** The original analytical request keeps metric meaning and scope intact. */
  request?: string;
};

export type DescribeReportingLeLensCardsInput = {
  datasetName: string;
  source: string;
  /** At most 16: the endpoint's own cap, mirrored here so a caller trims
   *  before sending rather than finding out from a response that ignored
   *  the rest. */
  cards: Array<DescribeReportingLeLensCard>;
};

export type DescribeReportingLeLensCardsResponse = {
  /** Keyed by the id sent, not by array order or count: a model failure
   *  answers with an empty array rather than an error, and a partial
   *  answer can name a subset of the ids sent. */
  descriptions: Array<{ id: string; description: string }>;
};

/**
 * One plain-English description per gate row. Purely decorative: the plan
 * this describes builds and approves exactly the same whether this call
 * succeeds, comes back empty, or never resolves at all. The endpoint itself
 * is fail-soft (a model failure answers 200 with an empty array rather than
 * an error), so nothing here adds a retry or an error state of its own; a
 * caller renders its own fallback text until, if ever, a description for
 * that row's id arrives.
 */
export function useDescribeReportingLeLensCards() {
  return useMutation({
    mutationFn: async (input: DescribeReportingLeLensCardsInput) => {
      return (await request(resolveReportingLeLensUrl(REPORTING_LE_LENS_DESCRIBE_PATH), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(input),
      })) as DescribeReportingLeLensCardsResponse;
    },
    retry: false,
  });
}
